import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Modal,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MIconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useDropzone } from "react-dropzone";
import api from "../../../utils/api";

const EditWorkOrder = ({ workOrderDetails, onUpdate, showAsButton = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState(workOrderDetails.status || "");
  const [isSaving, setIsSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [commentType, setCommentType] = useState("external");
  const [confirmExternalOpen, setConfirmExternalOpen] = useState(false);
  const [pendingType, setPendingType] = useState("external");
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getCurrentUserId = () => {
    const directId = Number(localStorage.getItem("currentUserId"));
    if (Number.isFinite(directId) && directId > 0) return directId;

    try {
      const storedUser = localStorage.getItem("user");
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      const parsedId = Number(parsed?.userId || parsed?.id);

      if (Number.isFinite(parsedId) && parsedId > 0) return parsedId;
    } catch (err) {
      console.error("Error parsing localStorage user:", err);
    }

    return null;
  };

  const currentUserId = getCurrentUserId();

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setIsLoadingStatuses(true);
        const res = await api.get(`/statuses/workorders`);
        setStatuses(res.data || []);
      } catch (error) {
        console.error("Failed to fetch statuses:", error);
        setStatuses([]);
      } finally {
        setIsLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    setUpdatedStatus(workOrderDetails.status || "");
    setCommentText("");
    setCommentType("external");
    setSelectedFiles([]);
  }, [workOrderDetails]);

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEditModalOpen = () => {
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setCommentText("");
    setCommentType("external");
    setSelectedFiles([]);
  };

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const handleCommentTypeChange = (type) => {
    if (type === "external") {
      setPendingType(type);
      setConfirmExternalOpen(true);
    } else {
      setCommentType("internal");
    }
  };

  const confirmExternalYes = () => {
    setCommentType(pendingType);
    setConfirmExternalOpen(false);
  };

  const confirmExternalCancel = () => {
    setCommentType("internal");
    setConfirmExternalOpen(false);
  };

  const appendUserField = (formData, fieldName) => {
    if (Number.isFinite(currentUserId) && currentUserId > 0) {
      formData.append(fieldName, currentUserId);
    }
  };

  const handleSubmitAll = async () => {
    const isResolved = updatedStatus.toLowerCase().includes("resolved");

    try {
      setIsSaving(true);

      const formData = new FormData();
      formData.append("status", updatedStatus);
      formData.append("property_id", workOrderDetails.property_id);
      formData.append("assign_to", workOrderDetails.assign_to || "");

      if (isResolved) {
        appendUserField(formData, "resolved_by");
      }

      if (commentText.trim()) {
        formData.append("comment_text", commentText.trim());
        formData.append("is_internal", commentType === "internal" ? 1 : 0);
        appendUserField(formData, "user_id");
      }

      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (selectedFiles.length > 0) {
        const uploadData = new FormData();
        uploadData.append("workorder_id", workOrderDetails.order_id);
        appendUserField(uploadData, "uploaded_by");

        if (commentText.trim()) {
          uploadData.append("comment_id", workOrderDetails.comment_id || null);
        }

        selectedFiles.forEach((file) => uploadData.append("upload_urls", file));

        await api.post(`/workorder-files`, uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setSnackbar({
        open: true,
        message: "Work order updated successfully!",
        severity: "success",
      });

      onUpdate?.();
      handleEditModalClose();
    } catch (error) {
      console.error("Error updating work order:", error);
      setSnackbar({
        open: true,
        message: "Failed to update work order.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const modalCardSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "92%", sm: 520 },
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: "rgba(255,255,255,0.98)",
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const sectionHeaderSx = {
    px: { xs: 2, sm: 2.5, md: 3 },
    pt: 2.5,
    pb: 1.5,
    borderBottom: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.02)",
  };

  const contentWrapSx = {
    p: { xs: 2, sm: 2.5, md: 3 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  return (
    <Box>
      {showAsButton ? (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Button
            variant="contained"
            onClick={handleEditModalOpen}
            startIcon={<AutorenewIcon />}
            sx={primaryButtonSx}
          >
            Change Status
          </Button>
        </Box>
      ) : (
        <>
          <IconButton
            onClick={handleMenuClick}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <MoreVertIcon />
          </IconButton>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleEditModalOpen}>Edit Status</MenuItem>
          </Menu>
        </>
      )}

      <Modal open={isEditModalOpen} onClose={handleEditModalClose}>
        <Box sx={modalCardSx}>
          <Box sx={sectionHeaderSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <EditNoteIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Edit Work Order Status
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Update the work order status, add comments, and attach supporting files.
            </Typography>
          </Box>

          <Box sx={contentWrapSx}>
            <FormControl fullWidth sx={{ mb: 2.25 }}>
              <InputLabel>Status</InputLabel>
              {isLoadingStatuses ? (
                <Box sx={{ display: "flex", alignItems: "center", minHeight: 56, pl: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Select
                  value={updatedStatus}
                  onChange={(e) => setUpdatedStatus(e.target.value)}
                  label="Status"
                  sx={textFieldSx}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.id} value={status.statusname}>
                      {status.statusname}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>

            <TextField
              label="Add Comment"
              multiline
              rows={4}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              fullWidth
              sx={{ ...textFieldSx, mb: 2.25 }}
            />

            <FormControl fullWidth sx={{ mb: 2.25 }}>
              <InputLabel>Comment Type</InputLabel>
              <Select
                value={commentType}
                onChange={(e) => handleCommentTypeChange(e.target.value)}
                label="Comment Type"
                sx={textFieldSx}
              >
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ mb: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#223142" }}>
              Attach Files
            </Typography>

            <Paper
              variant="outlined"
              {...getRootProps()}
              sx={{
                p: 2.5,
                border: "2px dashed #90caf9",
                borderRadius: "14px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f5faff",
                  borderColor: "#1976d2",
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? "Drop files here..."
                  : "Drag & drop files here, or click to select"}
              </Typography>
            </Paper>

            {selectedFiles.length > 0 && (
              <List
                dense
                sx={{
                  mt: 1.5,
                  maxHeight: 150,
                  overflowY: "auto",
                  border: "1px solid rgba(47,99,173,0.08)",
                  borderRadius: "12px",
                  backgroundColor: "rgba(47,99,173,0.02)",
                  px: 1,
                }}
              >
                {selectedFiles.map((file, idx) => (
                  <ListItem
                    key={`${file.name}-${idx}`}
                    sx={{
                      borderBottom:
                        idx !== selectedFiles.length - 1
                          ? "1px solid rgba(47,99,173,0.08)"
                          : "none",
                    }}
                  >
                    <ListItemText
                      primary={file.name}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: { color: "#223142", fontWeight: 500 },
                      }}
                    />
                    <ListItemSecondaryAction>
                      <MIconButton onClick={() => handleRemoveFile(idx)} size="small">
                        <DeleteIcon fontSize="small" />
                      </MIconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.25,
                mt: 3,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleEditModalClose}
                sx={secondaryButtonSx}
                disabled={isSaving}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmitAll}
                disabled={isSaving || isLoadingStatuses}
                sx={primaryButtonSx}
              >
                {isSaving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Submit Changes"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={confirmExternalOpen}
        onClose={confirmExternalCancel}
        PaperProps={{ sx: dialogPaperSx }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            color: "#223142",
            pt: 3,
          }}
        >
          <WarningAmberIcon sx={{ color: "#ed6c02" }} />
          Send to Homeowner?
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            External comments will be emailed to the homeowner. Continue?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
          <Button onClick={confirmExternalCancel} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={confirmExternalYes} variant="contained" sx={primaryButtonSx}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditWorkOrder;